import { useCallback, useEffect, useState } from "react";
import { ChevronDown, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import WarrantyTerms from "./WarrantyTerms";
import { hariIniISO } from "../../lib/tanggal";

const FIELDS = [
  ["umum", "Ketentuan umum", "Tampil di semua invoice penjualan, termasuk aksesoris."],
  ["new", "Unit New", "Ketentuan untuk unit baru."],
  ["second", "Unit Second", "Termasuk Second iBox, Inter, Bea Cukai, dan Promo."],
  ["aktivasi_imei", "Aktivasi IMEI", "Ketentuan layanan aktivasi IMEI."],
  ["sim_sticker", "SIM Sticker", "Ketentuan produk SIM Sticker."],
];

export default function WarrantySettings({ canUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [previewGroup, setPreviewGroup] = useState("second");
  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/faktur/garansi");
      setForm(data); setSaved(data);
    } catch { setError("Pengaturan garansi belum berhasil dimuat."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const dirty = !!form && (FIELDS.some(([key]) => form[key] !== saved?.[key]) ||
    JSON.stringify(form.duration_days) !== JSON.stringify(saved?.duration_days));
  const validDuration = !form || Object.values(form.duration_days || {}).every(v => v === null || (Number.isInteger(v) && v >= 0 && v <= 3650));
  const save = async () => {
    setSaving(true); setError("");
    try {
      const { data } = await api.put("/faktur/garansi", {
        revision: form.revision, ...Object.fromEntries(FIELDS.map(([key]) => [key, form[key]])),
        duration_days: form.duration_days || {},
      });
      setForm(data); setSaved(data);
      toast.success("Garansi disimpan. Berlaku untuk invoice baru.");
    } catch (e) {
      setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Garansi gagal disimpan. Coba lagi.");
    } finally { setSaving(false); }
  };
  const sections = form ? FIELDS.filter(([key]) => (key === "umum" || key === previewGroup || previewGroup === "semua") && form[key]?.trim())
    .map(([key, title]) => ({ key, title, text: form[key] })) : [];
  const purchaseDate = hariIniISO();
  const previewCoverage = form ? FIELDS.filter(([key]) => key !== "umum" && (key === previewGroup || previewGroup === "semua")).map(([key, title]) => {
    const days = form.duration_days?.[key] ?? null;
    const end = Number.isInteger(days) && days > 0 && days <= 3650
      ? new Date(Date.parse(`${purchaseDate}T00:00:00Z`) + (days - 1) * 86400000).toISOString().slice(0, 10) : null;
    return { id: key, key, title, item_name: `Contoh ${title}`, duration_days: days, start_date: purchaseDate,
      end_date: end, expires_at: end ? new Date(Date.parse(`${end}T00:00:00+07:00`) + 86400000).toISOString() : null };
  }) : [];
  return (
    <section className="mb-6 rounded-3xl border border-blue-100 bg-white shadow-sm no-print" data-testid="warranty-settings">
      <button type="button" onClick={() => setExpanded(v => !v)} aria-expanded={expanded} aria-controls="warranty-settings-body"
        data-testid="warranty-toggle" className="flex w-full items-center gap-3 p-5 text-left rounded-3xl focus-visible:ring-2 focus-visible:ring-blue-500">
        <span className="rounded-2xl bg-blue-50 p-3 text-blue-600"><ShieldCheck className="h-5 w-5" /></span>
        <span className="flex-1"><span className="block font-semibold text-slate-900">Garansi seluruh invoice</span>
          <span className="block mt-1 text-xs text-slate-500">Atur isi garansi untuk faktur cetak, struk, dan tautan pelanggan.</span></span>
        <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform motion-reduce:transition-none ${expanded ? "rotate-180" : ""}`} />
      </button>
      {error && <div role="alert" className="mx-5 mb-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">
        <p>{error}</p><button type="button" onClick={load} disabled={loading || saving} className="mt-2 font-semibold underline">Muat ulang pengaturan tersimpan</button>
      </div>}
      {expanded && <div id="warranty-settings-body" className="px-5 pb-5">
        <p className="mb-4 rounded-2xl bg-blue-50 p-4 text-sm leading-relaxed text-blue-900">
          Invoice yang sudah terbit menyimpan ketentuan saat diterbitkan. Perubahan di sini berlaku untuk invoice baru setelah disimpan.
          Isi ketentuan umum dan kategori yang diperlukan; kolom kosong tidak menambahkan janji garansi.
          Masa garansi dihitung sejak tanggal pembelian (WIB), dengan tanggal pembelian sebagai hari pertama.
        </p>
        {loading ? <p className="text-sm text-slate-500" role="status">Memuat garansi…</p> : form && <>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-5">
              {FIELDS.map(([key, label, hint]) => <label key={key} className="block">
                <span className="text-sm font-semibold text-slate-800">{label}</span>
                <span className="block my-1 text-xs text-slate-500">{hint}</span>
                {key !== "umum" && <span className="my-3 block rounded-xl border border-blue-100 p-3">
                  <span className="block text-xs font-medium mb-2">Masa garansi (hari)</span>
                  <input type="number" min="0" max="3650" step="1" inputMode="numeric"
                    aria-label={`Masa garansi ${label} (hari)`} data-testid={`warranty-days-${key}`}
                    value={form.duration_days?.[key] ?? ""} disabled={!canUpdate || saving} placeholder="Belum ditentukan"
                    onChange={e => setForm(f => ({ ...f, duration_days: { ...f.duration_days, [key]: e.target.value === "" ? null : Number(e.target.value) } }))}
                    className="w-full rounded-xl border border-blue-100 bg-white p-3 text-sm" />
                  <span className="block mt-2 text-xs text-slate-500">Kosong: belum ditentukan. 0: tanpa garansi. Isi 1–3.650 hari untuk mengaktifkan countdown.</span>
                </span>}
                <textarea value={form[key]} maxLength={8000} rows={4} readOnly={!canUpdate || saving}
                  data-testid={`warranty-${key}`} placeholder="Isi ketentuan garansi toko di sini…"
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  className="w-full resize-y rounded-2xl border border-blue-100 bg-slate-50 p-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400" />
                <span className="block text-right text-xs text-slate-400">{form[key].length}/8.000</span>
              </label>)}
            </div>
            <div className="min-w-0">
              <div className="rounded-2xl border border-slate-200 p-4 lg:sticky lg:top-6">
                <h3 className="font-semibold text-slate-800">Pratinjau isi garansi</h3>
                <p className="mt-1 mb-3 text-xs text-slate-500">Contoh isi untuk invoice baru. Invoice dengan beberapa kategori menampilkan semua ketentuan yang sesuai.</p>
                <label className="block text-xs text-slate-600">Barang pada contoh invoice
                  <select value={previewGroup} onChange={e => setPreviewGroup(e.target.value)} data-testid="warranty-preview-group"
                    className="mt-1 w-full rounded-xl border border-blue-100 bg-white p-2 text-sm">
                    {FIELDS.filter(([key]) => key !== "umum").map(([key, title]) => <option key={key} value={key}>{title}</option>)}
                    <option value="umum">Aksesoris / kategori lain</option><option value="semua">Semua kategori</option>
                  </select>
                </label>
                <WarrantyTerms warranty={{ sections, coverage: previewCoverage }} />
              </div>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-blue-50 pt-4">
            <p className="text-xs text-slate-500">{form.revision ? `Versi tersimpan ${form.revision}` : "Belum ada ketentuan tersimpan"}{dirty ? " · Ada perubahan belum disimpan" : ""}</p>
            {!validDuration && <p role="alert" className="text-sm text-red-600">Masa garansi harus berupa bilangan bulat 0–3.650 hari.</p>}
            {canUpdate ? <button type="button" onClick={save} disabled={!dirty || saving || !validDuration} data-testid="warranty-save"
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white disabled:opacity-40">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan garansi
            </button> : <p className="text-xs text-slate-500">Izin ubah Template Faktur diperlukan untuk menyimpan.</p>}
          </div>
        </>}
      </div>}
    </section>
  );
}
